import { CommonModule, NgClass, NgComponentOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  HostListener,
  Input,
  NO_ERRORS_SCHEMA,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-modal',
  imports: [CommonModule, NgClass, NgComponentOutlet],
  templateUrl: './modal.html',
  styleUrl: './modal.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class Modal implements OnChanges, AfterViewInit, OnDestroy {
  @Input() open = false;
  @Input() title = '';
  @Input() size: 'sm' | 'md' | 'md-1' | 'lg' | 'xl' | '2xl' = 'md';
  @Input() closeOnBackdrop = true;
  @Input() hasCustomHeader = false;
  @Input() hasCustomFooter = false;
  @Input() confirmButtonText: string = 'Aceptar';
  @Input() isSaving = false;
  /** Body as a flex column, so a child can fill the available height and scroll only part of its content */
  @Input() fillBody = false;

  @Input() bodyComponent?: any;
  @Input() bodyInputs: Record<string, unknown> = {};
  @Input() bodyOutputs: Record<string, (value?: any) => void> = {};

  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<unknown>();

  get dialogSizeClass(): string {
    switch (this.size) {
      case 'sm':
        return 'max-w-[420px]';
      case 'md':
        return 'max-w-[520px]';
      case 'md-1':
        return 'max-w-[640px]';
      case 'lg':
        return 'max-w-[1060px]';
      case 'xl':
        return 'max-w-[1020px]';
      case '2xl':
        return 'max-w-[900px]';
      default:
        return 'max-w-[700px]';
    }
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    if (this.open) this.close.emit();
  }

  onBackdrop() {
    this.close.emit();
  }
  @ViewChild('dynHost', { read: NgComponentOutlet })
  outlet?: NgComponentOutlet<any>;

  onConfirmClick() {
    const inst = this.outlet?.componentInstance as any;
    if (!inst) {
      return;
    }
    if (
      typeof inst.isValid === 'function' &&
      typeof inst.getValue === 'function'
    ) {
      if (!inst.isValid()) return;
      const dto = inst.getValue();

      // Include image file if component has getServiceImageFile or getProfessionalAvatarFile method
      if (typeof inst.getServiceImageFile === 'function') {
        const imageFile = inst.getServiceImageFile();
        if (imageFile) {
          (dto as any).imageFile = imageFile;
        }
      } else if (typeof inst.getProfessionalAvatarFile === 'function') {
        const avatarFile = inst.getProfessionalAvatarFile();
        if (avatarFile) {
          (dto as any).avatarFile = avatarFile;
        }
      }

      this.confirm.emit(dto);
      return;
    }
  }

  private outputSubs = new Map<string, Subscription>();

  ngAfterViewInit() {
    Promise.resolve().then(() => this.bindBodyOutputs());
  }

  ngOnChanges(_: SimpleChanges) {
    Promise.resolve().then(() => this.bindBodyOutputs());
  }

  ngOnDestroy() {
    this.clearOutputSubs();
  }

  private clearOutputSubs() {
    this.outputSubs.forEach((s) => s.unsubscribe());
    this.outputSubs.clear();
  }

  private bindBodyOutputs() {
    const inst = this.outlet?.componentInstance as any;
    if (!inst) return;

    this.clearOutputSubs();
    Object.entries(this.bodyOutputs).forEach(([prop, handler]) => {
      const emitter = inst[prop];
      console.log(`Modal: Looking for output '${prop}' in component:`, emitter ? 'found' : 'NOT FOUND', 'instance:', inst);
      if (emitter && typeof emitter.subscribe === 'function') {
        console.log(`Modal: Subscribing to '${prop}'`);
        const sub = emitter.subscribe((payload: any) => {
          try {
            console.log(`Modal: Received event from '${prop}':`, payload);
            handler(payload);
          } catch (e) {
            console.error(e);
          }
        });
        this.outputSubs.set(prop, sub);
      } else {
        console.warn(`Modal: Output '${prop}' not found or not subscribable`);
      }
    });
  }
}
